/**
 * Cmd/Ctrl+K command palette (Slice E4).
 *
 * Mounted once at the layout shell. Listens globally for the toggle
 * keybinding (suppressed while focus is inside editable text), surfaces
 * navigation commands keyed off the active tenant/project, and renders any
 * page-scoped sections that have registered via
 * `useRegisterCommandPaletteSection` (e.g. the workspace's jump-to-cell
 * items).
 */
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command';
import { ProjectCtx } from '@/hooks/project-context';
import { TenantCtx } from '@/hooks/tenant-context';
import { useSidebar } from '@/components/ui/sidebar';
import { isEditableTarget } from '@/lib/keyboard-shortcut-guards';
import { projectPath, tenantPath } from '@/lib/routes';

import {
  useCommandPaletteSections,
  type CommandPaletteItem,
} from './command-palette-registry-context';

interface NavigationCommand {
  readonly id: string;
  readonly label: string;
  readonly keywords: ReadonlyArray<string>;
  readonly to: string;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const tenant = useContext(TenantCtx);
  const project = useContext(ProjectCtx);
  const sections = useCommandPaletteSections();
  const { toggleSidebar } = useSidebar();
  const sidebarShortcut = useMemo(
    () =>
      typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/u.test(navigator.userAgent)
        ? '⌘B'
        : 'Ctrl B',
    [],
  );

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const isToggle =
        (event.key === 'k' || event.key === 'K') && (event.metaKey || event.ctrlKey);
      if (!isToggle) return;
      if (!open && isEditableTarget(event.target)) return;
      event.preventDefault();
      setOpen((current) => !current);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const runCommand = useCallback((action: () => void) => {
    setOpen(false);
    action();
  }, []);

  const navigationCommands = useMemo<ReadonlyArray<NavigationCommand>>(() => {
    if (tenant === null) return [];
    const items: NavigationCommand[] = [
      {
        id: 'nav-projects',
        label: 'Go to projects',
        keywords: ['projects', 'list', 'workspaces'],
        to: tenantPath(tenant.slug, '/projects'),
      },
      {
        id: 'nav-templates',
        label: 'Go to template library',
        keywords: ['templates', 'library'],
        to: tenantPath(tenant.slug, '/templates'),
      },
      {
        id: 'nav-dashboard',
        label: 'Go to dashboard',
        keywords: ['dashboard', 'home'],
        to: tenantPath(tenant.slug),
      },
    ];
    if (project !== null) {
      const projectId = project.project.project_id;
      items.push(
        {
          id: 'nav-project-overview',
          label: 'Go to current project',
          keywords: ['project', 'overview', project.project.display_name],
          to: projectPath(tenant.slug, projectId),
        },
        {
          id: 'nav-project-documents',
          label: 'Go to project documents',
          keywords: ['documents', 'files', 'results'],
          to: projectPath(tenant.slug, projectId, '/documents'),
        },
        {
          id: 'nav-project-execution-log',
          label: 'Go to execution log',
          keywords: ['activity', 'audit', 'extraction', 'support'],
          to: projectPath(tenant.slug, projectId, '/settings?tab=logs'),
        },
        {
          id: 'nav-project-templates',
          label: 'Go to project templates',
          keywords: ['templates', 'project'],
          to: projectPath(tenant.slug, projectId, '/templates'),
        },
      );
    }
    return items;
  }, [tenant, project]);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Type a command or search..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        {/* In-view actions first — the palette leads with what you can DO in
            the current surface; navigating away comes last. */}
        {sections.map((section, index) => (
          <SectionGroup
            key={section.id}
            heading={section.heading}
            items={section.items}
            showSeparator={index > 0}
            onRun={runCommand}
          />
        ))}
        {sections.some((section) => section.items.length > 0) ? <CommandSeparator /> : null}
        <CommandGroup heading="View">
          <CommandItem
            value="toggle sidebar collapse expand hide show"
            onSelect={() => {
              runCommand(toggleSidebar);
            }}
          >
            Toggle sidebar
            <CommandShortcut>{sidebarShortcut}</CommandShortcut>
          </CommandItem>
        </CommandGroup>
        {navigationCommands.length > 0 ? (
          <>
            <CommandSeparator />
            <CommandGroup heading="Navigation">
              {navigationCommands.map((command) => (
                <CommandItem
                  key={command.id}
                  value={`${command.label} ${command.keywords.join(' ')}`}
                  onSelect={() => {
                    runCommand(() => {
                      void navigate(command.to);
                    });
                  }}
                >
                  {command.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        ) : null}
      </CommandList>
    </CommandDialog>
  );
}

function SectionGroup({
  heading,
  items,
  showSeparator,
  onRun,
}: {
  readonly heading: string;
  readonly items: ReadonlyArray<CommandPaletteItem>;
  readonly showSeparator: boolean;
  readonly onRun: (action: () => void) => void;
}) {
  if (items.length === 0) return null;
  return (
    <>
      {showSeparator ? <CommandSeparator /> : null}
      <CommandGroup heading={heading}>
        {items.map((item) => (
          <CommandItem
            key={item.id}
            value={`${item.label} ${(item.keywords ?? []).join(' ')}`}
            onSelect={() => {
              onRun(item.onSelect);
            }}
          >
            {item.label}
            {item.shortcut !== undefined ? (
              <CommandShortcut>{item.shortcut}</CommandShortcut>
            ) : null}
          </CommandItem>
        ))}
      </CommandGroup>
    </>
  );
}
